// CoursePausePage.js
import React, { useState } from 'react';
import {
  Box, Typography, Grid, Paper, Button, Link, Chip, Divider,
  Accordion, AccordionSummary, AccordionDetails, List, ListItem, ListItemIcon, ListItemText,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow
} from '@mui/material';
import {
  Schedule, Pause, Refresh, InfoOutlined, HelpOutline, PlayCircleOutline, CheckCircle,
  Cancel, AddCircle, RemoveCircle, ThumbUpAltOutlined, AccountBalanceWalletOutlined
} from '@mui/icons-material';

// --- Reusable Sub-Components ---

const InfoBox = ({ icon, iconBg, children }) => (
  <Box sx={{ p: 2, mb: 2, display: 'flex', gap: 2, alignItems: 'center', bgcolor: 'background.default', borderRadius: '8px' }}>
    <Box sx={{ mt: 0.5, minWidth: 32, height: 32, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: iconBg }}>{icon}</Box>
    <Box>{children}</Box>
  </Box>
);

const FaqItem = ({ question, children }) => (
  <Accordion elevation={0} bordered>
    <AccordionSummary sx={{ bgcolor: 'background.default' }} expandIcon={<AddCircle color="primary" />}>
      <Typography variant="subtitle1" fontWeight={600}>{question}</Typography>
    </AccordionSummary>
    <AccordionDetails sx={{ bgcolor: 'background.default' }}>
      <Typography variant="body1" color="secondary.light">{children}</Typography>
    </AccordionDetails>
  </Accordion>
);

const FeatureTableRow = ({ feature, active, paused }) => (
  <TableRow>
    <TableCell>{feature}</TableCell>
    <TableCell align="center">{active}</TableCell>
    <TableCell align="center">{paused}</TableCell>
  </TableRow>
);

// --- Main Page Component ---

const CoursePauseDetails = () => {
  const [selectedAction, setSelectedAction] = useState('pause');

  return (
    <Paper sx={{ p: { xs: 2, md: 4 }, borderRadius: '12px' }}>

      <Grid container spacing={5}>
        <Grid item xs={12} lg={3}>
          <Paper variant="outlined" sx={{ padding: "20px 20px 12px 20px", borderRadius: '12px', mb: 4 }}>
            <Box display="flex" alignItems="center" gap={1} mb={1}><AccountBalanceWalletOutlined /><Typography variant="subtitle" fontWeight={700}>Total Credit Balance</Typography></Box>
            <Chip label="1 Credit = 1 Pause/ 1 Module Reset" size="small" sx={(theme) => ({ bgcolor: theme.palette.background.default, mb: 2 })} />
            <Box display="flex" alignItems="center" mb={1} gap="8px">
                <Typography variant="body1" fontWeight={600}>Credits Available: </Typography>
                <Typography fontWeight="bold" color="success.main">0</Typography>
                <InfoOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </Box>
            <Box display="flex" alignItems="center" mb={2} gap="8px">
                <Typography variant="body1" fontWeight={600}>Purchasable Credits Available: </Typography>
                <Typography fontWeight="bold" color="error.main">9</Typography>
                <InfoOutlined sx={{ fontSize: 16, verticalAlign: 'middle' }} />
            </Box>
            <Divider />
            <Link href="#" underline="hover" sx={{ display: 'block', mt: 1.5, textAlign: 'center', fontSize: "14px", fontWeight: 600 }}>View Credit Transactions</Link>
          </Paper>
          <Typography variant="subtitle" fontWeight={700}>Course Pause History</Typography>
          {[1, 2, 3].map(i => (
            <Paper key={i} variant="outlined" sx={{ p: 2, borderRadius: '8px', mb: 1.5 }}>
                <Box display={"flex"} gap="8px">
                    <Typography fontWeight={600}>Pause on:</Typography>
                    <Typography color="secondary.light" fontWeight={500}>18 Nov 2023</Typography>
                </Box>
                <Box display={"flex"} gap="8px">
                    <Typography fontWeight={600}>Reason:</Typography>
                    <Typography color="secondary.light" fontWeight={500}>To complete backlog</Typography>
                </Box>
                <Box display={"flex"} gap="8px">
                    <Typography fontWeight={600}>Status:</Typography>
                    <Typography color="secondary.light" fontWeight={500}>Accepted</Typography>
                </Box>
            </Paper>
          ))}
        </Grid>

        <Grid item xs={12} lg={9}>
          <Typography variant="title" color="secondary.light" fontWeight={700}>What is Course Pause?</Typography>
          <Typography color="secondary.light" fontWeight={500} my={1}>In case of any family, health, personal emergency, or excessive backlog we understand that you may want to pause your course for some time. Course pause feature allows you to pause your course at any moment and rejoin the course where you left it on pause.</Typography>
          <InfoBox icon={<InfoOutlined sx={{ color: '#dc3545' }} />} iconBg="#f8d7da">
            {/* <Typography variant="body1" fontSize={"14px"} fontWeight={700}>IMP:</Typography> */}
            <Typography variant="body1" fontSize={"14px"} fontWeight={500}>During the Exam Period, course pause will not normally be permitted. However, once you have completed the exam, you will be able to take a course pause if desired.</Typography>
            {/* <Typography variant="body1"><b>IMP:</b> During the Exam Period, course pause will not normally be permitted. However, once you have completed the exam, you will be able to take a course pause if desired.</Typography> */}
          </InfoBox>
          <InfoBox icon={<HelpOutline sx={{ color: '#6f42c1' }} />} iconBg="#e9d5ff">
            <Typography variant="body1" fontSize={"14px"} fontWeight={700}>When to use Course Pause?</Typography>
            <Typography variant="body1" fontSize={"14px"} fontWeight={500}>Appropriate to use when you wish to take a break from the course.</Typography>
          </InfoBox>
          <InfoBox icon={<PlayCircleOutline sx={{ color: '#0d6efd' }} />} iconBg="#cfe2ff">
            <Typography variant="body1" fontSize={"14px"} fontWeight={700}>Check this out</Typography>
            <Typography variant="body1" fontSize={"14px"} fontWeight={500}>In this Introduction Video to Pause and Reset features, you will learn about how these features work and when to use them.</Typography>
          </InfoBox>

          <Typography variant="h6" mt={2}>How does it work?</Typography>
          <List dense sx={{ '& .MuiListItem-root': { alignItems: 'flex-start', fontSize: "14px", fontWeight: 500 }, '& .MuiListItemIcon-root': { minWidth: 20, fontSize: "14px", fontWeight: 500 } }}>
            <ListItem><ListItemIcon>•</ListItemIcon><ListItemText>Course pause follows an erratic system where 1 Pause = X Pause.</ListItemText></ListItem>
            <ListItem><ListItemIcon>•</ListItemIcon><ListItemText>2 free Credits are given to you to pause.</ListItemText></ListItem>
          </List>

          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mt: 4 }}>
            <TableContainer component={Paper} variant="outlined" sx={{ flex: 2, minWidth: 300, borderRadius: '8px' }}>
                <Table>
                  <TableHead sx={{ bgcolor: 'background.lightBlue' }}><TableRow><TableCell colSpan={3}><Typography variant="subtitle1" fontWeight={600}>Status of features during the pause</Typography></TableCell></TableRow><TableRow><TableCell>Features</TableCell><TableCell align="center">Course Active</TableCell><TableCell align="center">Course Paused</TableCell></TableRow></TableHead>
                  <TableBody>
                    <FeatureTableRow feature="Live Classes" active={<CheckCircle color="success" />} paused={<CheckCircle color="success" />} />
                    <FeatureTableRow feature="Scratch" active={<CheckCircle color="success" />} paused={<>Optional <InfoOutlined sx={{fontSize: 14, verticalAlign: 'middle'}}/></>} />
                    <FeatureTableRow feature="Existing Projects" active={<CheckCircle color="success" />} paused={<Cancel color="error" />} />
                  </TableBody>
                </Table>
            </TableContainer>
            <TableContainer component={Paper} variant="outlined" sx={{ flex: 1, minWidth: 250, borderRadius: '8px' }}>
                <Table>
                    <TableHead sx={{ bgcolor: 'background.lightBlue' }}><TableRow><TableCell colSpan={2}><Typography variant="subtitle1" fontWeight={600}>Pause Credit Pricing Policy</Typography></TableCell></TableRow><TableRow><TableCell>Duration</TableCell><TableCell>Cost Calculation</TableCell></TableRow></TableHead>
                    <TableBody>
                        <TableRow><TableCell>First 2 Credits</TableCell><TableCell>Free</TableCell></TableRow>
                        <TableRow><TableCell>Post 2 Credits (One Credit)</TableCell><TableCell>INR 5000 per credit</TableCell></TableRow>
                    </TableBody>
                </Table>
            </TableContainer>
          </Box>
          
          <Typography variant="h6" mt={4}>Why do we charge for curriculum credit?</Typography>
          <Typography variant="body2" color="secondary.light" mt={1} mb={3}>Learning is a time-bound way for motivated people to get their careers going, we would hence want to make sure the process suits that the students finish the course in the prescribed time.</Typography>

          <Box mt={2}><InfoBox icon={<ThumbUpAltOutlined sx={{ color: '#0d6efd' }} />} iconBg="#cfe2ff"><Typography><b>Recommendation:</b> Better to take a 2-pause Reset. You save a non-refundable amount, this will enable you to continue the course without any gaps.</Typography></InfoBox></Box>
          
          <Typography variant="h6" mt={4} mb={2}>FAQs</Typography>
          <Box display="flex" flexDirection="column" gap={2}>
            <FaqItem question="What is the minimum & maximum duration for availing course pause? Can I take a pause for 1 month instead of 2 or 5?">When you're opting for a Pause, you'll find that it's more cost-effective to utilize the entire block of a pause period as a single unit, especially since the start of your course is fixed. Moreover, these pause periods have been designed around our module payment plans and your existing batch schedule. This means these pause periods are determined by the scheduling of your next batch, which usually occurs around day 15, and also by the module payment due dates and module launches. Typically, it's best to use the entire pause period to stay on track (or get ahead) of your course workload.</FaqItem>
            <FaqItem question="By resuming my modules am I resubmitting my course?">No, this is not a resubmission. It's a continuation of your course.</FaqItem>
          </Box>
        </Grid>
      </Grid>
      
      <Box display="flex" justifyContent="flex-end" mt={4} borderTop={1} borderColor="divider" pt={3}>
        <Button variant="contained" color="error">Pause Course</Button>
      </Box>
    </Paper>
  );
};

export default CoursePauseDetails;